import { cn } from '@/shared/ui/utils/tailwind';

export interface ListItemBadge {
  label: string;
  variant?: 'default' | 'accent';
}

export interface ListItemCardProps {
  accentColor?: string;
  firstName: string;
  lastName?: string;
  badges?: ListItemBadge[];
  description?: string;
  /** Caps the description to this many lines, for sources (e.g. crawler text) that can run long enough to bury the name above it. Unset renders unclamped. */
  descriptionClamp?: number;
  metadata?: string[];
  onClick?: () => void;
  className?: string;
  children?: React.ReactNode;
  /**
   * Controls rendered inside the card but *outside* its button.
   *
   * A clickable card is a `<button>`, and a button cannot contain another
   * one: nesting them is invalid HTML, and React says so at runtime. Any
   * child that is itself interactive — a stage picker, a WhatsApp link —
   * belongs here, where it is a sibling of the card's own target rather
   * than a region of it.
   */
  footer?: React.ReactNode;
}

/**
 * Thin colored bar along the card's left edge, used when `accentColor` is set.
 */
function AccentBar({ color }: { color: string }) {
  return (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute left-[var(--space-2)] top-[var(--space-2)] bottom-[var(--space-2)] w-[3px] rounded-full"
      style={{ backgroundColor: color }}
    />
  );
}

/**
 * Renders one badge chip, styled as an accent or neutral pill per `badge.variant`.
 */
function BadgeChip({ badge }: { badge: ListItemBadge }) {
  return (
    <span
      className={cn(
        'shrink-0 rounded-[var(--radius-sm)] px-[var(--space-2)] py-[var(--space-0-5)] text-[length:var(--text-meta)] font-semibold leading-[var(--leading-tight)]',
        badge.variant === 'accent'
          ? 'bg-[var(--seed-base)]/8 text-[var(--seed-base)]'
          : 'bg-[var(--surface-tertiary)] text-[var(--text-secondary)]',
      )}
    >
      {badge.label}
    </span>
  );
}

/**
 * Renders one metadata entry, optionally prefixed with a separator dot.
 */
function MetadataItem({ item, showDot }: { item: string; showDot: boolean }) {
  return (
    <span className="flex items-center gap-[var(--space-2)]">
      {showDot && <span aria-hidden="true" className="inline-block h-[3px] w-[3px] rounded-full bg-[var(--text-tertiary)]" />}
      <span className="text-[length:var(--text-meta)] font-medium text-[var(--text-tertiary)]">{item}</span>
    </span>
  );
}

/**
 * Generic list-row card: name, optional accent bar, badges/metadata row,
 * and a description -- the shared shell behind client and property list rows.
 */
export function ListItemCard({
  accentColor, firstName, lastName, badges, description, descriptionClamp, metadata, onClick, className, children, footer,
}: ListItemCardProps) {
  const Tag = onClick ? 'button' : 'div';
  const fullName = lastName ? `${firstName} ${lastName}` : firstName;
  const hasTier2 = (badges && badges.length > 0) || (metadata && metadata.length > 0);

  const body = (
    <>
      {accentColor && !footer && <AccentBar color={accentColor} />}
      <span className="text-[length:var(--text-sm)] font-bold leading-[var(--leading-tight)] tracking-[var(--tracking-tight)] text-[var(--text-primary)]">{fullName}</span>
      {hasTier2 && (
        <div className="flex flex-wrap items-center gap-x-[var(--space-2)] gap-y-[var(--space-1)]">
          {badges?.map((b) => <BadgeChip key={b.label} badge={b} />)}
          {metadata?.map((item, i) => <MetadataItem key={item} item={item} showDot={i > 0 || Boolean(badges?.length)} />)}
        </div>
      )}
      {description && (
        <p
          className="text-[length:var(--text-xs)] font-normal leading-[var(--leading-snug)] text-[var(--text-secondary)]"
          style={descriptionClamp ? {
            display: '-webkit-box',
            WebkitLineClamp: descriptionClamp,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          } : undefined}
        >
          {description}
        </p>
      )}
      {children}
    </>
  );

  const chrome = cn(
    'relative flex w-full flex-col gap-[var(--space-1)] rounded-(--radius-lg) border border-[var(--border-subtle)] bg-[var(--surface-primary)] py-[var(--space-3)] pr-[var(--space-4)] text-left shadow-[var(--shadow-sm)] transition-colors',
    accentColor ? 'pl-[var(--space-5)]' : 'pl-[var(--space-4)]',
  );

  /*
   * No footer: one element, exactly as before. Every existing list renders
   * through this path, so adding the slot cannot have moved anything.
   */
  if (!footer) {
    return (
      <Tag
        type={onClick ? 'button' : undefined}
        onClick={onClick}
        className={cn(chrome, onClick && 'cursor-pointer active:bg-[var(--surface-secondary)]', className)}
      >
        {body}
      </Tag>
    );
  }

  /*
   * With a footer the card's chrome moves to a plain wrapper and the button
   * becomes a transparent region inside it. The visual is the same card; what
   * changes is that the footer is now a sibling of the tap target instead of
   * a button inside a button.
   */
  return (
    <div className={cn(chrome, 'gap-[var(--space-2)]', className)}>
      {accentColor && <AccentBar color={accentColor} />}
      <Tag
        type={onClick ? 'button' : undefined}
        onClick={onClick}
        className={cn(
          'flex w-full flex-col gap-[var(--space-1)] border-none bg-transparent p-0 text-left',
          onClick && 'cursor-pointer',
        )}
      >
        {body}
      </Tag>
      {footer}
    </div>
  );
}
